import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { modalActions } from "../../constants";
import {
	decrementLoaderCount,
	incrementLoaderCount,
} from "../../store/actions";
import { convertCamelCaseToTitle } from "../../utils";
import { findFormErrors } from "./dataForm";
import { DataModalBody } from "./dataModalBody";
import { EditorButtons } from "./editorButtons";
import { BlockInput, handleDataFormSubmit } from "..";

export const DataFormModal = ({
	isModalOpen,
	setIsModalOpen,
	editorSetting,
	initialModalData,
	isAutoOpen = true,
	refreshData = () => {},
}) => {
	const dispatch = useDispatch();
	const { loaderCount } = useSelector((state) => state.app);

	const [currentModalData, setCurrentModalData] = useState(
		JSON.parse(JSON.stringify(initialModalData)),
	);
	const [modalErrors, setModalErrors] = useState([{}]);

	const setCurrentModalDataAtIndex = (index) => (field) => (value) => {
		setCurrentModalData((modalData) => {
			modalData[index][field] = value;
			return modalData;
		});
	};

	const setCurrentModalErrorsAtIndex = (index) => (field, value) => {
		setModalErrors((modalData) => {
			modalData[index][field] = value;
			return modalData;
		});
	};

	const addForm = () => {
		setCurrentModalData((modalData) =>
			modalData.concat({ ...modalData.slice(-1)[0] }),
		);
		setModalErrors((modalData) =>
			modalData.concat({ ...modalData.slice(-1)[0] }),
		);
	};

	const handleClose = () => {
		setIsModalOpen(false);
		setCurrentModalData([{}]);
	};

	const handleFormSubmit = (apiFunctionSubmit) => () => {
		switch (editorSetting?.action) {
			case modalActions.MULTI_CREATE:
				let isErrorsFound = false;

				// get our new errors
				const newErrorsArray = currentModalData.map((form) => {
					const [formErrors, isErrorInForm] = findFormErrors(
						form,
						editorSetting,
					);
					isErrorsFound = isErrorsFound || isErrorInForm;
					return formErrors;
				});

				if (isErrorsFound) {
					setModalErrors(newErrorsArray);
				} else {
					handleClose();
					dispatch(incrementLoaderCount());
					apiFunctionSubmit(
						[...currentModalData],
						[...initialModalData],
					).finally(() => {
						dispatch(decrementLoaderCount());
						refreshData();
					});
				}

				break;
			case modalActions.CREATE:
			case modalActions.DELETE:
			case modalActions.EDIT:
			default:
				handleDataFormSubmit({
					data: currentModalData[0],
					setting: editorSetting,
					handleSuccess: () => {
						handleClose();
						dispatch(incrementLoaderCount());
						apiFunctionSubmit(
							JSON.parse(JSON.stringify(currentModalData[0])),
							JSON.parse(JSON.stringify(initialModalData[0])),
						).finally(() => {
							dispatch(decrementLoaderCount());
							refreshData();
						});
					},
					handleError: (formErrors) => setModalErrors([formErrors]),
				});

				break;
		}
	};

	useEffect(() => {
		const listener = (event) => {
			if (
				(event.code === "Enter" || event.code === "NumpadEnter") &&
				event.ctrlKey &&
				isModalOpen
			) {
				event.preventDefault();
				handleFormSubmit(editorSetting?.apiFunctionSubmit)();
			}
		};

		document.addEventListener("keydown", listener);
		return () => {
			document.removeEventListener("keydown", listener);
		};
	}, [editorSetting, isModalOpen]);

	const firstUpdate = useRef(true);

	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}

		setCurrentModalData(JSON.parse(JSON.stringify(initialModalData)));
		setModalErrors([{}]);

		// currently broken
		if (editorSetting?.isGetData && false) {
			switch (editorSetting.action) {
				case "edit":
					editorSetting
						.apiFunctionGet({ ...currentModalData[0] })
						.then((data) => {
							setCurrentModalData([data]);
						})
						.finally(() => {});
					break;
				case "create":
				default:
					break;
			}
		}
		if (!isAutoOpen) {
			return;
		}

		setIsModalOpen(true);
	}, [initialModalData]);

	const formatModalTitle = () =>
		`${convertCamelCaseToTitle(editorSetting?.action)} ${
			initialModalData[0]?.name ? `: ${initialModalData[0]?.name}` : ""
		}`;

	return (
		<Modal
			as={Modal.Dialog}
			centered
			size="lg"
			show={isModalOpen}
			onHide={handleClose}
		>
			<BlockInput blocking={loaderCount > 0} />
			<Modal.Header>
				<Modal.Title className="h6">
					{editorSetting?.formatModalTitle
						? editorSetting.formatModalTitle(initialModalData[0])
						: formatModalTitle()}
				</Modal.Title>
				<Button
					variant="close"
					aria-label="Close"
					onClick={handleClose}
				/>
			</Modal.Header>
			<Modal.Body className="py-0">
				{editorSetting?.formatBodyMessage ? (
					<div className="mt-3">
						{editorSetting.formatBodyMessage(initialModalData[0])}
					</div>
				) : null}
				<DataModalBody
					editorSetting={editorSetting}
					currentModalData={currentModalData}
					modalErrors={modalErrors}
					setCurrentModalDataAtIndex={setCurrentModalDataAtIndex}
					setCurrentModalErrorsAtIndex={setCurrentModalErrorsAtIndex}
					addForm={addForm}
				/>
			</Modal.Body>
			<Modal.Footer>
				<EditorButtons
					editorSetting={editorSetting}
					currentModalData={currentModalData}
					handleFormSubmit={handleFormSubmit}
				/>
			</Modal.Footer>
		</Modal>
	);
};
